/**
 * 全局弹窗提示组件
 */

const toastContainerId = 'toast-container';

/**
 * 弹窗选项
 */
export interface ToastOptions {
  title: string;
  message?: string;
  duration?: number; // 自动关闭时间（毫秒），0 表示不自动关闭
  type?: 'info' | 'success' | 'warning' | 'error';
  actions?: Array<{
    label: string;
    primary?: boolean;
    onClick: () => void;
  }>;
}

/**
 * 显示弹窗
 */
export function showToast(options: ToastOptions): void {
  // 确保容器存在
  let container = document.getElementById(toastContainerId) as HTMLDivElement;
  if (!container) {
    container = document.createElement('div');
    container.id = toastContainerId;
    document.body.appendChild(container);
  }

  // 创建弹窗元素
  const toast = document.createElement('div');
  toast.className = `toast toast-${options.type || 'info'}`;

  const typeIcons = {
    info: 'ℹ️',
    success: '✓',
    warning: '⚠️',
    error: '✕'
  };

  const actionsHtml = options.actions?.map(action => `
    <button class="toast-action ${action.primary ? 'toast-action-primary' : ''}" data-action="${action.label}">
      ${action.label}
    </button>
  `).join('') || '';

  toast.innerHTML = `
    <div class="toast-content">
      <div class="toast-icon">${typeIcons[options.type || 'info']}</div>
      <div class="toast-body">
        <div class="toast-title">${escapeHtml(options.title)}</div>
        ${options.message ? `<div class="toast-message">${escapeHtml(options.message)}</div>` : ''}
      </div>
    </div>
    ${actionsHtml ? `<div class="toast-actions">${actionsHtml}</div>` : ''}
  `;

  container.appendChild(toast);

  // 绑定按钮事件
  options.actions?.forEach(action => {
    const btn = toast.querySelector(`[data-action="${CSS.escape(action.label)}"]`) as HTMLButtonElement;
    btn?.addEventListener('click', () => {
      action.onClick();
      removeToast(toast);
    });
  });

  // 触发进入动画
  requestAnimationFrame(() => {
    toast.classList.add('toast-show');
  });

  // 自动关闭
  if (options.duration && options.duration > 0) {
    setTimeout(() => {
      removeToast(toast);
    }, options.duration);
  }
}

/**
 * 移除弹窗
 */
function removeToast(toast: HTMLElement): void {
  toast.classList.remove('toast-show');
  toast.classList.add('toast-hide');
  setTimeout(() => {
    toast.remove();
  }, 300);
}

/**
 * HTML 转义
 */
function escapeHtml(text: string): string {
  const div = document.createElement('div');
  div.textContent = text;
  return div.innerHTML;
}

/**
 * 便捷方法
 */
export const toast = {
  info: (title: string, message?: string, duration = 3000) =>
    showToast({ title, message, duration, type: 'info' }),

  success: (title: string, message?: string, duration = 2000) =>
    showToast({ title, message, duration, type: 'success' }),

  warning: (title: string, message?: string, duration = 3000) =>
    showToast({ title, message, duration, type: 'warning' }),

  error: (title: string, message?: string) =>
    showToast({ title, message, duration: 0, type: 'error' }),

  confirm: (title: string, message: string, onConfirm: () => void, onCancel?: () => void) =>
    showToast({
      title,
      message,
      type: 'warning',
      actions: [
        { label: '取消', onClick: () => onCancel?.() },
        { label: '确定', primary: true, onClick: onConfirm }
      ]
    })
};
